// NewsTalk (c) Copyright 2023 by Qwixotic
// 
// File:   NewsTalkClient.js
// Author: Percipient Reverence
// Date:   21-Mar-2023
//
// Description:
//
//   This is the main frontend module for NewsTalk.  It lists the sections of a media outlet.
//   Sections.js might have been a better name, but I chose this name because it is the main entry
//   point to the website.

import React, {useState, useEffect} from 'react'
import {useParams} from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import { DataGrid, GridRowsProp, GridColDef } from '@mui/x-data-grid';
import NewspaperIcon from '@mui/icons-material/Newspaper';

// Local imports
import { Link, MediaOutletImageLink, ArticleItem } from './lib/Components'
import { getCookie, myFetch } from './lib/Util'

// Display the page for a Media outlet, which includes a list of all its sections.  Since initially
// there is only one media outlet, the Washington Post, this is just a list of all the sections for
// the Washington Post.


export const MediaOutletEP = () => {
  // Create some state
  let [MediaOutlet, mediaOutletSet] = useState({})

  const Params = useParams()
    
  let MOID = Params.MOID
      ? parseInt(Params.MOID)
      : getCookie('MOID')
  if (!MOID || MOID <= 1) {
    MOID = 0
  }

  // Fetch the media outlet information, including list of sections, according to the MOID setting.
  // If there is no valid MOID setting then some media outlet will be chosen, and it's
  // deterministic, but we can't know which one.

  useEffect(() => {
    myFetch('/nt/MediaOutlet/' + MOID)
      .then(res => res.json())
      .then(mediaOutletSet)
  }, [MOID])

  // The above can return before all the data is ready, and then returns again when it is, which is
  // disturbing, but I guess that's the reality

  if (!MediaOutlet || !MediaOutlet.Sections) {
    return (
      <>
        Loading...
      </>
    )
  }

  let Rows: GridRowsProp = MediaOutlet.Sections.map((Section) => {
    Section.MediaOutlet = MediaOutlet // It's circular, but it makes other things easy, it won't
                                      // hurt anything, and I'll never follow the circle
    if (Section.LastArticle) {
      Section.LastArticle.Section = Section // Same
    }
    return (
      {id:            Section.SID,
       DisplayOrder:  Section.DisplayOrder,
       SID:           Section.SID,
       Sections:      Section,
       ACount:        Section.ACount,
       LastArticle:   Section.LastArticle
      })})

  // The "hide: true" property for hiding a column that used to be permitted in this array became
  // deprecated and was finally removed.  You now have to instead use the columnVisibilityModel
  // property with the <DataGrid> component itself to specify which columns should be hidden.
  const Columns: GridColDef[] = [
    {field: 'id'           },
    {field: 'DisplayOrder' },
    {field: 'SID'          },
    {field: 'Sections', headerName: 'Discussion Sections', flex: 20,
     renderCell: (params) => (<SectionItem Section={params.value}/>),
     sortComparator: (v1, v2) => v1.Name.localeCompare(v2.Name)},
    {field: 'ACount', headerName: 'Number Articles', flex: 15, headerAlign: 'center', align: 'center'}, // Original
    {field: 'LastArticle',  headerName: 'Most Recent Article Discussion', flex: 45,
     renderCell: (params) => (<MediaOutletArticleItem Article={params.value}/>),
     sortComparator: (v1, v2) => v1.Time-v2.Time},
  ]

  return (
    <>
      <MediaOutletHeader MediaOutlet={MediaOutlet}/>
      <DataGrid rows={Rows} columns={Columns} autoHeight={true} hideFooter={true} 
                getRowHeight={() => {return 'auto'}}
                columnVisibilityModel={{id: false, DisplayOrder: false, SID: false}}/>
    </>
  )
}

const MediaOutletArticleItem = (props) => {
  const Article = props.Article
  let retVal = null
  if (Article) {
    Article.threadToolTip = 'Discussion thread for this article'
    Article.articleToolTip = 'Link to this article at the ' + Article.Section.MediaOutlet.Name
    Article.noCommentsYet = 'No discussion yet'
    retVal = (
      <ArticleItem {...Article}/>
    )
  }
  return retVal
}

const SectionItem = (props) => {
  const Section = props.Section
  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{width: '100%'}}>
      <Grid item align='left'>
        <Tooltip title={'Discussion threads for the ' + Section.Name + ' section of the ' + Section.MediaOutlet.Name} placement='top'>
          <Link href={'/Section/' + Section.SID} fontSize='1.2rem' sx={{margin: '10px 0px 10px 0px'}}>{Section.Name}</Link>
        </Tooltip>
      </Grid>
      <Grid item align='right'>
        <Tooltip title={'Link to ' + Section.Name + ' section at the ' + Section.MediaOutlet.Name} placement='top'>
          <IconButton href={Section.URL}>
            <NewspaperIcon color='primary'/>
          </IconButton>
        </Tooltip>
      </Grid>
    </Stack>
  )
}

// Appears at the top of every page specific to a media outlet
const  MediaOutletHeader = (props) => {
  return (
    <Grid container sx={{marginTop: '10px', marginBottom: '10px'}}>
      <Grid item align='left'>
        <MediaOutletImageLink MediaOutlet={props.MediaOutlet} margin={{'marginRight': '5px'}} padding={{'padding': '7px'}}/>
      </Grid>
      <Grid sx={{marginLeft: 'auto', marginRight: 'auto'}}>
        <Typography display='flex' alignItems='center' fontSize='2.0rem' fontWeight='bold' sx={{height: '100%'}}>
          Sections
        </Typography>
      </Grid>
    </Grid>
  )
}
